import React, { useMemo } from 'react';
import {
  Modal,
  ModalBody,
  ModalContent,
  Spacer,
  useDisclosure,
} from '@nextui-org/react';
import { SidebarIcon as Icon } from './SidebarIcon';
import Sidebar, { SidebarItem } from './SidebarTypes';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import useDataStore from 'common/src/stores/useDataStore';
import useFilterStore from 'common/src/stores/useFiltersStore';
import SpeciesFilter from '../../../../../common/src/components/filters/SpeciesFilter';
import SeasonFilter from '../../../../../common/src/components/filters/SeasonFilter';
import DivisionFilter from '../../../../../common/src/components/filters/DivisionFilter';
import useWarningsStore from '../../../store/warningsStore';
import useExporterLabelsStore from '../../../store/useExporterLabelsStore';
import SettlementWarningsModal from '../../SettlementWarningsModal';
import useBrand from 'common/src/hooks/useBrand';
import ProfileMenu from './ProfileMenu';
import { splitPinnedItems } from './pinnedItems';

interface MobileSidebarProps {
  location: string;
  sectionItemsWithTeams: SidebarItem[];
  onSelect: (key: string) => void;
  onLogout: () => void;
  /** Solo se pasa si el usuario aún no activó la verificación en dos pasos. */
  onOpenTwoFactor?: () => void;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  hideBurger?: boolean;
}

const MobileSidebar: React.FC<MobileSidebarProps> = ({
  location,
  sectionItemsWithTeams,
  onSelect,
  onLogout,
  onOpenTwoFactor,
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange,
  hideBurger = false,
}) => {
  const { userProfile } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const { labels, loading: labelsLoading } = useExporterLabelsStore();
  const { assets: brandAssets } = useBrand();
  const {
    codesMissingMapping,
    settlementLabelsMissingInMappings,
    settlementVarietiesOutsideSpecies,
    unclassifiedExporterCostsCount,
  } = useWarningsStore();
  const {
    isOpen: internalIsOpen,
    onOpen: internalOnOpen,
    onOpenChange: internalOnOpenChange,
  } = useDisclosure();
  const {
    isOpen: isWarningsOpen,
    onOpen: onWarningsOpen,
    onOpenChange: onWarningsOpenChange,
  } = useDisclosure();
  const sidebarWidth = 288;

  // Use controlled state if provided, otherwise use internal state
  const isOpen =
    controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const onOpen =
    controlledIsOpen !== undefined
      ? () => controlledOnOpenChange?.(true)
      : internalOnOpen;
  const onOpenChange = controlledOnOpenChange || internalOnOpenChange;

  const canLoadLabels = useMemo(
    () =>
      Boolean(
        userProfile?.exporter_id && selectedSpecies?.id && selectedSeason
      ),
    [userProfile?.exporter_id, selectedSeason, selectedSpecies?.id]
  );

  const hasNoDeclaredLabels =
    canLoadLabels && !labelsLoading && labels.length === 0;

  const activeWarningsCount =
    (codesMissingMapping.length > 0 ? 1 : 0) +
    (settlementLabelsMissingInMappings.length > 0 ? 1 : 0) +
    (settlementVarietiesOutsideSpecies.length > 0 ? 1 : 0) +
    (unclassifiedExporterCostsCount > 0 ? 1 : 0) +
    (hasNoDeclaredLabels ? 1 : 0);

  // Extraer iniciales de manera segura
  const findHref = React.useCallback(
    (items: SidebarItem[], key: string): string | undefined => {
      for (const item of items) {
        if (item.key === key && item.href) return item.href;

        if (item.items) {
          const childHref = findHref(item.items, key);
          if (childHref) return childHref;
        }
      }

      return undefined;
    },
    []
  );

  const selectedKey = location.split('/')[1] || 'daily-market';
  const { main: mainItems, pinned: pinnedItems } = useMemo(
    () => splitPinnedItems(sectionItemsWithTeams),
    [sectionItemsWithTeams]
  );
  const onSidebarSelect = (
    key: string | React.SyntheticEvent<HTMLUListElement, Event>
  ) => {
    if (typeof key === 'string') {
      onSelect(key);
      if (findHref(sectionItemsWithTeams, key)) {
        onOpenChange(false);
      }
    }
  };

  return (
    <div className='relative '>
      {!hideBurger && (
        <div className='fixed top-0 right-0 p-4 z-40'>
          <button
            className='shadow-md bg-content1 p-2 rounded-lg'
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
          >
            <Icon
              className='text-default-500'
              height={24}
              icon='solar:hamburger-menu-outline'
              width={24}
            />
          </button>
        </div>
      )}
      <Modal
        classNames={{
          base: 'justify-start m-0 p-0 h-dvh max-h-full w-[var(--sidebar-width)] z-[50]',
          wrapper: 'items-start   justify-start !w-[var(--sidebar-width)]',
          body: 'p-0',
          closeButton: 'z-50',
        }}
        isOpen={isOpen}
        motionProps={{
          variants: {
            enter: {
              x: 0,
              transition: {
                duration: 0.3,
                ease: 'easeOut',
              },
            },
            exit: {
              x: -500,
              transition: {
                duration: 0.4,
                ease: 'easeOut',
              },
            },
          },
        }}
        radius='none'
        scrollBehavior='inside'
        style={
          {
            '--sidebar-width': `${sidebarWidth}px`,
          } as React.CSSProperties & Record<'--sidebar-width', string>
        }
        onOpenChange={onOpenChange}
      >
        <ModalContent>
          <ModalBody>
            {/* Todo el panel se desliza junto: en el teléfono no alcanza para
                dejar fijos el encabezado y el pie con la lista en medio. */}
            <div className='relative flex min-h-full w-72 flex-1 flex-col p-6'>
              <div className='flex items-center gap-2 px-3'>
                <div className='flex min-w-0 flex-1 items-center'>
                  <img
                    src={brandAssets.fullLogoLight}
                    alt={brandAssets.name}
                    className='h-auto w-full object-contain object-left opacity-100 dark:hidden'
                  />
                  <img
                    src={brandAssets.fullLogoDark}
                    alt={brandAssets.name}
                    className='hidden h-auto w-full object-contain object-left opacity-100 dark:block'
                  />
                </div>
              </div>
              <Spacer y={8} />
              {/* Perfil: abre el menú de la cuenta (idioma, tema, cerrar sesión). */}
              <ProfileMenu
                onLogout={onLogout}
                onOpenTwoFactor={onOpenTwoFactor}
              />

              {/* Filtro de especies */}
              {!(
                userProfile?.roles?.includes('superadmin') ||
                userProfile?.roles?.includes('document_helper')
              ) && (
                <div className='mt-4 px-3 w-full'>
                  <SpeciesFilter className='w-full' size='sm' />
                </div>
              )}

              {/* Filtro de especies */}
              {!(
                userProfile?.roles?.includes('superadmin') ||
                userProfile?.roles?.includes('document_helper')
              ) && (
                <div className='mt-4 px-3 w-full'>
                  <SeasonFilter collapsed={false} />
                </div>
              )}
              {!(
                userProfile?.roles?.includes('superadmin') ||
                userProfile?.roles?.includes('document_helper')
              ) && (
                <div className='mt-4 px-3 w-full empty:hidden'>
                  <DivisionFilter className='w-full' size='sm' />
                </div>
              )}
              <div className='mt-3 px-3 w-full'>
                {activeWarningsCount === 0 ? (
                  <div className='rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950 dark:border-emerald-800 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300'>
                    No hay alertas activas.
                  </div>
                ) : (
                  <div className='rounded-lg border border-rose-200 bg-rose-50 dark:bg-rose-950 dark:border-rose-800 px-3 py-2 text-sm text-rose-700 dark:text-rose-300 flex items-center justify-between gap-2'>
                    <span>Hay {activeWarningsCount} alertas activas.</span>
                    <button
                      type='button'
                      className='text-xs font-medium underline'
                      onClick={onWarningsOpen}
                    >
                      Mostrar
                    </button>
                  </div>
                )}
              </div>
              <SettlementWarningsModal
                isOpen={isWarningsOpen}
                onOpenChange={onWarningsOpenChange}
              />

              <div className='py-6'>
                <Sidebar
                  selectedKeys={[selectedKey]}
                  defaultSelectedKey={selectedKey}
                  items={mainItems}
                  onSelect={onSidebarSelect}
                  aria-label='Navigation Menu' // Add aria-label
                />
              </div>

              {/* Configuración, fija al fondo como en escritorio. */}
              {pinnedItems.length ? (
                <div className='mt-auto'>
                  <Sidebar
                    selectedKeys={[selectedKey]}
                    defaultSelectedKey={selectedKey}
                    items={pinnedItems}
                    onSelect={onSidebarSelect}
                    aria-label='Navigation Menu'
                  />
                </div>
              ) : null}
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default MobileSidebar;
